import { useMemo } from 'react';

import uniq from 'lodash/uniq';

import {
  Checkbox,
  Divider,
  Scroller,
  Tooltip,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';

import { useClmCompatibleFamiliesPicker } from './ClmCompatibleFamiliesPickerProvider';
import { InstanceChip } from './InstanceChip';
import { INSTANCE_COMPATIBILITY_MAPS } from './InstanceCompatibilityMap';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type CompatibleFamiliesSetCardProps = {
  familiesSet: Map<string, string[]>;
  setName: string;
};

const CompatibleFamiliesSetCard = ({
  familiesSet,
  setName,
}: CompatibleFamiliesSetCardProps) => {
  const {
    availableInstances,
    selectedInstances,
    baseInstance,
    setSelectedInstances,
  } = useClmCompatibleFamiliesPicker();

  const familiesWithValidInstances = useMemo(() => {
    const availableSet = new Set(availableInstances);

    return Array.from(familiesSet.entries())
      .map(
        ([familyName, instances]) =>
          [
            familyName,
            instances.filter((instance) => availableSet.has(instance)),
          ] as [string, string[]]
      )
      .filter(([_, instances]) => instances.length > 0);
  }, [availableInstances, familiesSet]);

  const validSetInstances = useMemo(() => {
    return familiesWithValidInstances
      .flatMap(([_, instances]) => instances)
      .filter((i) => i !== baseInstance);
  }, [baseInstance, familiesWithValidInstances]);

  const isEntireSetSelected = useMemo(() => {
    const selectedSet = new Set(selectedInstances);
    return validSetInstances.every((instance) => selectedSet.has(instance));
  }, [selectedInstances, validSetInstances]);

  const handleOnSetSelect = () => {
    if (isEntireSetSelected) {
      setSelectedInstances(
        selectedInstances.filter((i) => !validSetInstances.includes(i))
      );
    } else {
      setSelectedInstances(uniq([...selectedInstances, ...validSetInstances]));
    }
  };

  const handleInstanceSelect = (instance: string) => {
    if (selectedInstances.includes(instance)) {
      setSelectedInstances(selectedInstances.filter((i) => i !== instance));
    } else {
      setSelectedInstances(uniq([...selectedInstances, instance]));
    }
  };

  if (!familiesWithValidInstances.length) {
    return null;
  }

  return (
    <Box
      border={
        isEntireSetSelected
          ? tokens.theme.border.selection.primary
          : tokens.theme.border.structural.neutral
      }
      p={12}
      borderRadius="4px"
    >
      <Stack direction="row" gap={8} alignItems="flex-start">
        <Checkbox
          size="small"
          onChange={handleOnSetSelect}
          checked={isEntireSetSelected}
          testId={setName}
        />
        <Stack>
          {familiesWithValidInstances.map(([familyName, instances], index) => {
            return (
              <Stack key={familyName}>
                <Typography variant="P12B" pb={4}>
                  {familyName}
                </Typography>
                <Stack gap={8} direction="row" flexWrap="wrap">
                  {instances.map((instance) => (
                    <Tooltip
                      title="Cannot deselect chosen base instance"
                      placement="right"
                      key={instance}
                      wrapIf={instance === baseInstance}
                    >
                      <div>
                        <InstanceChip
                          disabled={instance === baseInstance}
                          instance={instance}
                          checked={
                            instance === baseInstance ||
                            selectedInstances.includes(instance)
                          }
                          onClick={() => handleInstanceSelect(instance)}
                          testId={
                            instance === baseInstance
                              ? 'base-instance'
                              : undefined
                          }
                        />
                      </div>
                    </Tooltip>
                  ))}
                </Stack>
                {index !== familiesWithValidInstances.length - 1 && (
                  <Divider
                    color={tokens.theme.border.structural.neutralSubtle}
                    sx={{ my: 8 }}
                  />
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </Box>
  );
};

export const CompatibleInstancesSelector = () => {
  const { cluster } = useCluster();
  const { baseInstance } = useClmCompatibleFamiliesPicker();

  const showSelector = !!baseInstance;

  const compatibleSets = useMemo(() => {
    if (!baseInstance) {
      return [];
    }
    const compatibilityMap =
      INSTANCE_COMPATIBILITY_MAPS[cluster.providerType] ??
      new Map<string, Map<string, string[]>>();

    return Array.from(compatibilityMap.entries()).filter(([, innerMap]) =>
      Array.from(innerMap.values()).some((instances) =>
        instances.includes(baseInstance)
      )
    );
  }, [baseInstance, cluster.providerType]);

  return (
    <Stack
      gap={4}
      flexGrow={1}
      overflow="auto"
      data-testid="compatible-instances-selector"
    >
      <Typography variant="P12M">Select compatible families</Typography>
      <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
        Pick compatible families from matching CPU sets to guarantee seamless
        workload migration.
      </Typography>
      {showSelector && (
        <>
          <Typography
            variant="L10M"
            color={tokens.theme.fg.structural.secondary}
            pb={4}
            pt={20}
          >
            compatible families
          </Typography>
          <Scroller>
            <Stack gap={11} flexGrow={1}>
              {compatibleSets.map(([setName, familiesSet]) => (
                <CompatibleFamiliesSetCard
                  familiesSet={familiesSet}
                  key={setName}
                  setName={setName}
                />
              ))}
            </Stack>
          </Scroller>
        </>
      )}
    </Stack>
  );
};
